import React from "react";
import { Target, Banknote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { IMAGES } from "@/lib/site";

const VALUES = [
  { image: "Excellence.png", title: "Excellence", text: "We hold every release to banking-grade standards of quality and security." },
  { image: "Partnership.png", title: "Partnership", text: "We work as an extension of your team, transparent and accountable throughout." },
  { image: "Pragmatism.png", title: "Pragmatism", text: "We favour proven, maintainable solutions over unnecessary complexity." },
  { image: "Leadership.png", title: "Ownership", text: "We take end-to-end responsibility for outcomes, not just activity." },
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">About Sphere IT Solutions</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
              A consulting partner built around Core Banking.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              Sphere IT Solutions is a specialist core banking consultancy. We exist to help
              banks and financial institutions run, modernize and transform their core platforms —
              with the depth, discipline and domain understanding that mission-critical systems demand.
            </p>
          </Reveal>
        </div>
      </section>

      {/* IMAGE + STORY */}
      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal>
            <img
              src="/media/about_intro_optimized.avif"
              alt="Sphere IT Solutions consulting team in a corporate meeting"
              className="rounded-3xl w-full aspect-[16/7] object-cover shadow-xl ring-1 ring-slate-200"
              loading="lazy"
            />
          </Reveal>
          <div className="mt-16 grid lg:grid-cols-3 gap-12">
            <Reveal>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Our story</h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-2 space-y-5 text-lg text-slate-600 leading-relaxed">
              <p>
                Founded by core banking practitioners, Sphere IT Solutions was created to do one thing
                exceptionally well: deliver core banking programmes that succeed. Over more
                than 20 years, our team has architected, migrated and supported core platforms for
                retail, corporate and private banks across multiple regions.
              </p>
              <p>
                We have seen what makes core banking programmes succeed — and what makes them fail.
                That experience is distilled into our accelerators, governance models and delivery
                playbooks, so every client benefits from lessons earned across dozens of engagements.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="mission-section">
        <img src={`${process.env.PUBLIC_URL}/media/Mission_background.png`} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-white/75" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <div className="gold-icon-tile inline-flex items-center justify-center w-12 h-12 rounded-xl text-white shadow-md mb-5">
              <Target className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Our mission</h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              To be the most trusted core banking delivery partner for banks and financial
              institutions — making core modernization predictable, secure and genuinely value-driven.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="gold-icon-tile inline-flex items-center justify-center w-12 h-12 rounded-xl text-white shadow-md mb-5">
              <Banknote className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Our specialization</h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              We focus exclusively on the core banking ecosystem. That depth means we understand not just
              the technology, but the regulatory, operational and data realities of running a core
              banking platform in production.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal className="mx-auto max-w-2xl text-center" data-testid="values-heading">
            <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">Our values</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              The principles behind every engagement
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div data-testid="value-card" className="raised-surface flex h-full flex-col overflow-hidden rounded-[var(--radius-large)] bg-white hover:-translate-y-1 transition-all duration-300">
                  <img src={`${process.env.PUBLIC_URL}/media/${v.image}`} alt="" className="h-48 w-full object-cover sm:h-52 lg:h-48" loading="lazy" />
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="text-lg font-semibold text-brand font-heading">{v.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default About;
